import type { CatalogModel, Lane, Residency } from "./types.js";

export const LANE_NAMES: Record<Lane, string> = {
  fireworks: "Fireworks",
  baseten: "Baseten",
  fireworks_us: "Fireworks US",
  anthropic: "Anthropic",
  anthropic_us: "Anthropic US",
  bedrock: "Amazon Bedrock",
};

const LANE_HOSTING: Record<Lane, "US" | "global"> = {
  fireworks: "global",
  baseten: "US",
  fireworks_us: "US",
  anthropic: "global",
  anthropic_us: "US",
  bedrock: "US",
};

/** The one line the policy and tier cards say about the pin-only lanes. */
export const PIN_ONLY_COPY =
  "Anthropic and Anthropic US are pin-only until Anthropic approves zero data retention, and Amazon Bedrock (Claude, US only) is pin-only by choice: no policy walks them and no rescue lands on them. Pin a workspace's tier below to use one.";

/** Where a lane is hosted, as its badge reads. */
export function hostingBadge(lane: Lane): "US" | "global" {
  return LANE_HOSTING[lane];
}

/** Price multiplier badge for the US lanes that charge a premium. */
export function premiumBadge(lane: Lane): string | null {
  if (lane === "fireworks_us") return "×1.5";
  if (lane === "anthropic_us" || lane === "bedrock") return "×1.1";
  return null;
}

/** The lanes a workspace may pin: its policy's order, then the pin-only
 * lanes its residency allows. */
export function pinnableLanes(
  order: Lane[],
  pinOnly: Lane[] | undefined,
  residency: Residency,
): Lane[] {
  const extra = (pinOnly ?? []).filter((lane) => residency !== "us" || LANE_HOSTING[lane] === "US");
  return [...order, ...extra.filter((lane) => !order.includes(lane))];
}

function isLane(value: string): value is Lane {
  return value in LANE_NAMES;
}

export function laneName(lane: string): string {
  return isLane(lane) ? LANE_NAMES[lane] : lane;
}

export function policyName(residency: Residency): string {
  return residency === "us" ? "US only" : "Unrestricted";
}

export function ordinal(n: number): string {
  if (n === 1) return "1st";
  if (n === 2) return "2nd";
  if (n === 3) return "3rd";
  return `${n}th`;
}

/** Picker option label: `<label> · $in/$out` per million tokens. */
export function priceLabel(model: CatalogModel): string {
  return `${model.label} · $${model.inputPerMillion.toFixed(2)}/$${model.outputPerMillion.toFixed(2)}`;
}

/** Disabled picker option shown when a lane's price list is empty. */
export const EMPTY_CATALOG_LABEL = "No models on the price list";

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString();
}

/** Datalist entry for a workspace; unique because slugs are unique. */
export function workspaceLabel(ws: { name: string; slug: string }): string {
  return `${ws.name} (${ws.slug})`;
}
